import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from 'reactstrap'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import PropTypes from 'prop-types'
import {
  fetchNetSuiteAssetById,
  clearNetSuiteAssets,
} from 'store/netSuiteAssetManager'
import { clearUsers } from 'store/css-admin/userManager'
import { selectNetSuiteAssetById } from 'store/netSuiteAssetManager/selector'
import ErrorBoundary from 'components/ErrorBoundary'
import Loading from 'components/Loading/Loading'
import EditLicenseModal from 'components/EditLicenseModal'
import LicenseHeader from './LicenseHeader'
import LicenseSummary from './LicenseSummary'
import { LicenseProps } from './LicenseRouter'
import { RootState } from 'types'
import { NetSuiteAssetManagerState } from 'store/netSuiteAssetManager/reducer'

const LicenseManager: React.FC<LicenseProps> = ({ match }) => {
  const dispatch = useDispatch()

  const [showEditModal, setShowEditModal] = useState<boolean>(false)

  const id = parseInt(match.params.id, 10)

  const { netSuiteAsset, isLoading } = useSelector((state: RootState) => {
    const netSuiteState: NetSuiteAssetManagerState = state.netSuiteAssetManager
    return {
      netSuiteAsset: selectNetSuiteAssetById(netSuiteState, id),
      isLoading: netSuiteState?.isLoading,
    }
  })
  const isSaving = useSelector(
    (state: RootState) => state.licenseManager?.isSaving,
  )

  const loadNetSuiteAsset = useCallback(() => {
    dispatch(fetchNetSuiteAssetById(id))
  }, [dispatch, id])

  //mount
  useEffect(() => {
    loadNetSuiteAsset()
  }, [loadNetSuiteAsset])

  // Refetch asset when modal closes to ensure we have the latest data
  const prevShowEditModal = useRef(showEditModal)
  useEffect(() => {
    // If modal was open and now it's closed, refetch the asset
    if (prevShowEditModal.current && !showEditModal) {
      loadNetSuiteAsset()
    }
    prevShowEditModal.current = showEditModal
  }, [showEditModal, loadNetSuiteAsset])

  const backToSearch = () => {
    dispatch(clearUsers())
    dispatch(clearNetSuiteAssets())
    dispatch(push('/css-admin/licenses'))
  }

  const toggleEditModal = () => setShowEditModal(!showEditModal)
  return (
    <ErrorBoundary>
      <p>
        <Button color="primary" onClick={backToSearch} outline>
          <i className="fas fa-chevron-left" />
          &nbsp; Back to Search
        </Button>
      </p>
      <Loading isLoading={!!isLoading} />
      <Loading isLoading={!!isSaving} message="Saving..." />
      {netSuiteAsset && (
        <div>
          <LicenseHeader
            netSuiteAsset={netSuiteAsset}
            toggleEditModal={toggleEditModal}
          />
          <LicenseSummary netSuiteAsset={netSuiteAsset} />
        </div>
      )}
      {showEditModal && netSuiteAsset && (
        <EditLicenseModal
          isOpen={showEditModal}
          toggle={toggleEditModal}
          netSuiteAsset={netSuiteAsset}
        />
      )}
    </ErrorBoundary>
  )
}

LicenseManager.propTypes = {
  match: PropTypes.object.isRequired,
}

export default LicenseManager
